import * as def from '@/definitions'
import { TextInfo } from '@/utilityModels/textInfo'
import { TextLayer } from './textLayer';
import { UuidHelper } from '@/helper/uuidHelper';
import { StringHelper } from '@/helper/stringHelper';
import { UnitHelper } from '@/helper/unitHelper';
import { FontManager } from '@/model/fontManager/fontManager';
import { DebugHelper } from '@/helper/debugHelper';
import { Vector2 } from '@/utilityModels/vector2';
import { Rectangle } from '@/utilityModels/rectangle';
import { SimpleTextLayerHelper } from '@/helper/simpleTextLayerHelper';

// シンプルテキストレイヤー
// 文字ごとではなくレイヤーごとに一つの文法設定を持つ
export class SimpleTextLayer extends TextLayer
{
    public get layerType() : string { return "simpleText"; }

    // 自動(文字を枠に合わせたサイズにする)
    private _autoSize : boolean = false;
    public get autoSize() : boolean { return this._autoSize; }
    public set autoSize(autoSize : boolean) {this._autoSize = autoSize; }

    // 自動折り返し(ある程度で折り返す)
    private _autoNewLine: boolean = false;
    public get autoNewLine(): boolean { return this._autoNewLine; }
    public set autoNewLine(autoNewLine: boolean) { this._autoNewLine = autoNewLine; }

    // 行間(%)
    private _lineSpacing: number = 120;
    public get lineSpacing(): number { return this._lineSpacing; }
    public set lineSpacing(lineSpacing) { this._lineSpacing = lineSpacing; }

    // 文字方向
    // 0: 横書き
    // 1: 縦書き
    private _textOrientation: number = 0;
    public get textOrientation(): number { return this._textOrientation; }
    public set textOrientation(textOrientation: number) { this._textOrientation = textOrientation; }

    // 水平揃え
    // 0: 左
    // 1: 中央
    // 2: 右
    // 3: 均等
    private _horizontalAlignment : number = 0;
    public get horizontalAlignment() : number { return this._horizontalAlignment; }
    public set horizontalAlignment(horizontalAlignment : number) { this._horizontalAlignment = horizontalAlignment; }

    // 垂直揃え
    // 0: 上
    // 1: 中央
    // 2: 下
    private _verticalAlignment : number = 0;
    public get verticalAlignment() : number { return this._verticalAlignment; }
    public set verticalAlignment(verticalAlignment : number) { this._verticalAlignment = verticalAlignment; }

    // 語頭追加
    private _prefix: string = "";
    public get prefix(): string { return this._prefix; }
    public set prefix(prefix: string) { this._prefix = prefix; }

    // 語尾追加
    private _suffix: string = "";
    public get suffix(): string { return this._suffix; }
    public set suffix(suffix: string) { this._suffix = suffix; }

    // 文法情報
    private _baseTextInfo : TextInfo = new TextInfo();
    public get baseTextInfo() { return this._baseTextInfo; }
    public set baseTextInfo(baseTextInfo) { this._baseTextInfo = baseTextInfo; }

    // プロフィールキー
    private _profileKey : string = "";
    public get profileKey() { return this._profileKey; }
    public set profileKey(profileKey) { this._profileKey = profileKey; }

    // オートフォントサイズ
    private _autoFontSize : number = 14.0;
    public get autoFontSize() { return this._autoFontSize; }
    public set autoFontSize(autoFontSize) { this._autoFontSize = autoFontSize; }

    // 自動改行のときのテキスト
    private _autoLineText : string = "";
    public get autoLineText() { return this._autoLineText; }
    public set autoLineText(autoLineText) { this._autoLineText = autoLineText; }

    // 入力中のテキスト(保存しない)
    public edittingText : string = "";

    // 作成
    public static create(text : string, x : number, y : number, width : number, height : number) : SimpleTextLayer
    {
        var layer = new SimpleTextLayer();
        layer.text = text;

        layer.autoSize = true;
        layer.autoNewLine = false;

        layer.horizontalAlignment = 0;
        layer.verticalAlignment = 0;
        layer.x = x;
        layer.y = y;
        layer.width = width;
        layer.height = height;
        layer.insertionId = UuidHelper.getUuid();

        return layer;
    }

    // 複製
    public clone()
    {
        var cloned = new SimpleTextLayer();
        this.cloneParameter(cloned);
        return cloned;
    }

    public cloneParameter(layer : SimpleTextLayer)
    {
        layer.autoSize = this._autoSize;
        layer.autoNewLine = this._autoNewLine;
        layer.lineSpacing = this._lineSpacing;
        layer.textOrientation = this._textOrientation;
        layer.horizontalAlignment = this._horizontalAlignment;
        layer.verticalAlignment = this._verticalAlignment;
        layer.prefix = this._prefix;
        layer.suffix = this._suffix;
        layer.profileKey = this._profileKey;
        layer.baseTextInfo = this._baseTextInfo.clone();
        layer.autoFontSize = this._autoFontSize;
        layer.autoLineText = this._autoLineText;

        super.cloneParameter(layer);
    }


    // JSON
    public toJSON()
    {
        var jsonObj = {};
        jsonObj = this.saveParameterToJson(jsonObj);
        return jsonObj;
    }

    public saveParameterToJson(jsonObject)
    {
        jsonObject.autoSize = this._autoSize;
        jsonObject.autoNewLine = this._autoNewLine;
        jsonObject.lineSpacing = this._lineSpacing;
        jsonObject.textOrientation = this._textOrientation;
        jsonObject.horizontalAlignment = this._horizontalAlignment;
        jsonObject.verticalAlignment = this._verticalAlignment;
        jsonObject.prefix = this._prefix;
        jsonObject.suffix = this._suffix;
        jsonObject.profileKey = this._profileKey;
        jsonObject.baseTextInfo = this._baseTextInfo;
        jsonObject.autoFontSize = this._autoFontSize;
        jsonObject.autoLineText = this._autoLineText;

        jsonObject = super.saveParameterToJson(jsonObject);

        return jsonObject;
    }

    public static createFromJsonData(jsonData : any) : SimpleTextLayer
    {
        var layer = new SimpleTextLayer();
        layer.loadParameterFromJson(jsonData);
        return layer;
    }

    public loadParameterFromJson(jsonData)
    {
        if(jsonData.autoSize != null) { this._autoSize = jsonData.autoSize; }
        if(jsonData.autoNewLine != null) { this._autoNewLine = jsonData.autoNewLine; }
        if(jsonData.lineSpacing != null) { this._lineSpacing = jsonData.lineSpacing; }
        if(jsonData.textOrientation != null) { this._textOrientation = jsonData.textOrientation; }
        if(jsonData.horizontalAlignment != null) { this._horizontalAlignment = jsonData.horizontalAlignment; }
        if(jsonData.verticalAlignment != null) { this._verticalAlignment = jsonData.verticalAlignment; }
        if(jsonData.prefix != null) { this._prefix = jsonData.prefix; }
        if(jsonData.suffix != null) { this._suffix = jsonData.suffix; }
        if(jsonData.profileKey != null) { this._profileKey = jsonData.profileKey; }
        if(jsonData.baseTextInfo != null) { this._baseTextInfo = TextInfo.createFromJsonData(jsonData.baseTextInfo); }
        if(jsonData.autoFontSize != null) { this._autoFontSize = jsonData.autoFontSize; }
        if(jsonData.autoLineText != null) { this._autoLineText = jsonData.autoLineText; }
 
        super.loadParameterFromJson(jsonData);
    }

    // 座標から対応する文字選択情報を得る
    public calcPositionFromPoint(x : number, y : number, fontManager : FontManager) : { lineIndex : number, characterIndex : number, isAfter : boolean }
    {
        var lineIndex = 0;
        var characterIndex = 0;
        var isAfter = false;
        if(this.textOrientation == 1)
        {
            // 縦書き
            var rectArray = SimpleTextLayerHelper.getRenderingTextRectArrayVertical(this, fontManager, 0, 0, true);
            for(var i = rectArray.length - 1; i >= 0; --i)
            {
                var rect = rectArray[i];
                if((rect.rect.y <= y || rect.character == 0) && rect.rect.x + rect.rect.width >= x)
                {
                    lineIndex = rect.line;
                    characterIndex = rect.character;
                    isAfter = (rect.rect.y + rect.rect.height / 2 < y);
                    break;
                }
            }
        }
        else
        {
            // 横書き
            var rectArray = SimpleTextLayerHelper.getRenderingTextRectArray(this, fontManager, 0, 0, true);
            for(var i = rectArray.length - 1; i >= 0; --i)
            {
                var rect = rectArray[i];
                if((rect.rect.x <= x || rect.character == 0) && rect.rect.y <= y)
                {
                    lineIndex = rect.line;
                    characterIndex = rect.character;
                    isAfter = (rect.rect.x + rect.rect.width / 2 < x);
                    break;
                }
            }
        }
        
        return { lineIndex : lineIndex, characterIndex : characterIndex, isAfter : isAfter };
    }
}